/**
 * Client identification utilities for determining which theme to load
 */

export type ClientIdentificationMethod = 'subdomain' | 'url-parameter' | 'hostname';

export interface ClientIdentificationConfig {
  method: ClientIdentificationMethod;
  parameter?: string; // For URL parameter method
  subdomainMapping?: Record<string, string>; // Maps subdomain to client ID
}

export class ClientIdentifier {
  private static config: ClientIdentificationConfig = {
    method: this.getDefaultMethod(),
    parameter: 'client',
    subdomainMapping: {
      'app': 'default',
      'localhost': 'default'
    }
  };

  private static getDefaultMethod(): ClientIdentificationMethod {
    // Development mode: use URL parameter for easy testing
    if (import.meta.env.DEV || import.meta.env.VITE_ALLOW_URL_CLIENT_SWITCHING === 'true') {
      return 'url-parameter';
    }
    
    // Production mode: use subdomain method
    return 'subdomain';
  }

  static configure(config: Partial<ClientIdentificationConfig>): void {
    this.config = { ...this.config, ...config };
  }

  static getClientId(): string {
    switch (this.config.method) {
      case 'subdomain':
        return this.getClientFromSubdomain();
      case 'url-parameter':
        return this.getClientFromUrlParameter();
      case 'hostname':
        return this.getClientFromHostname();
      default:
        return 'default';
    }
  }

  private static getClientFromSubdomain(): string {
    const hostname = window.location.hostname;
    
    // Extract subdomain
    const parts = hostname.split('.');
    if (parts.length <= 2) {
      // No subdomain (e.g., localhost, example.com)
      return this.config.subdomainMapping?.[hostname] || 'default';
    }
    
    const subdomain = parts[0];
    return this.config.subdomainMapping?.[subdomain] || subdomain;
  }

  private static getClientFromUrlParameter(): string {
    if (!this.config.parameter) {
      return 'default';
    }

    const urlParams = new URLSearchParams(window.location.search);
    const clientId = urlParams.get(this.config.parameter);
    return clientId || 'default';
  }

  private static getClientFromHostname(): string {
    const hostname = window.location.hostname;
    return this.config.subdomainMapping?.[hostname] || hostname;
  }

  /**
   * Get the current client identification method
   */
  static getMethod(): ClientIdentificationMethod {
    return this.config.method;
  }

  /**
   * Add or update subdomain mapping
   */
  static addSubdomainMapping(subdomain: string, clientId: string): void {
    if (!this.config.subdomainMapping) {
      this.config.subdomainMapping = {};
    }
    this.config.subdomainMapping[subdomain] = clientId;
  }

  /**
   * Get all current mappings
   */
  static getMappings(): ClientIdentificationConfig {
    return { ...this.config };
  }

  /**
   * Validate if a client ID is supported
   */
  static isClientSupported(clientId: string): boolean {
    // For now, we'll support any client ID and let the theme loader handle fallbacks
    // This can be extended to validate against a known list of clients
    return Boolean(clientId && clientId.length > 0);
  }
}

/**
 * Hook for React components to get the current client ID
 * Note: This is a simple function, not a React hook, since it doesn't use React state
 */
export const useClientId = (): string => {
  return ClientIdentifier.getClientId();
};
